<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { telemetry, logger } from '$lib';
  import DataSelection from '$lib/components/DataSelection/DataSelection.svelte';
  import FilterBar from '$lib/components/FilterBar.svelte';
  import Filters from '$lib/components/Filters.svelte';
  import DataSelectionFilters from '$lib/components/DataSelection/DataSelectionFilters.svelte';
  import OrderForm from '$lib/components/OrderForm.svelte';
  import PricingBar from '$lib/components/PricingBar.svelte';
  import Loading from '$lib/components/Loading.svelte';
  import Results from '$lib/components/Results.svelte';
  import IndustryInfo from '$lib/components/IndustryInfo.svelte';
  import ScreenView from '$lib/components/ScreenView.svelte';
  import { Layers, RotateCcw, Sparkles, X } from 'lucide-svelte';
  import Switcher from '$lib/components/Switcher.svelte';
  import NotificationContainer from '$lib/components/NotificationContainer.svelte';
  import AIChatOverlay from '$lib/components/AIChatOverlay.svelte';
  import AIChatContent from '$lib/components/AIChatContent.svelte';
  import LanguageSwitcher from '$lib/components/LanguageSwitcher.svelte';
  import AccountMenu from '$lib/components/AccountMenu.svelte';
  import ShareSelection from '$lib/components/ShareSelection.svelte';
  import SaveSelectionToList from '$lib/components/SaveSelectionToList.svelte';
  import CustomerList from '$lib/components/CustomerList.svelte';
  import StartOver from '$lib/components/StartOver.svelte';
  import ConfirmDialog from '$lib/components/ConfirmDialog.svelte';
  import { customerList, type CompanyList } from '$lib/api/lists';
  import { goto } from '$app/navigation';
  import { applicationMode } from '$lib/stores/mode';
  import { sidebarOpen, modeSwitchDialog, resetDialog, exportHistoryOpen } from '$lib/stores/ui';
  import { aiChatActive, aiModeActive } from '$lib/stores/ai';
  import { queryRunning, contactRoles, contactTitles, hasSearched } from '$lib/stores/selection';
  import { guidedActive, guidedStep } from '$lib/stores/guided';
  import { mixedResults } from '$lib/stores/mixedResults';
  import { selectedIndustryCentroids } from '$lib/stores/data';
  import { submitFilters } from '$lib/api/filters';
  import { openShare, shareParam } from '$lib/api/share';
  import { industryProfile } from '$lib/stores/industryProfile';
  import { showDataSelectionModal, shouldOpenDataSelection } from '$lib/stores/modal';
  import { turnstile } from '$lib/turnstile';
  import { notifications } from '$lib/stores/notifications';
  import { restoreCustomerList } from '$lib/api/lists';
  import { resetSelection } from '$lib/stores/reset';
  import { restoreSession, signedIn } from '$lib/stores/auth';
  import { env } from '$lib/config/environment';
  import {
    canOrder,
    isDataView,
    isFrontDoor,
    isFrontScreen,
    screenFor,
    showsSelection,
    sidebarFor
  } from '$lib/stores/screen';
  import GuidedSearch from '$lib/components/Guided/GuidedSearch.svelte';
  import MemberHome from '$lib/components/MemberHome.svelte';
  import MemberNav from '$lib/components/MemberNav.svelte';
  import SavedListView from '$lib/components/SavedListView.svelte';
  import ExportHistory from '$lib/components/ExportHistory.svelte';
  import TopbarSearch from '$lib/components/TopbarSearch.svelte';
  import { openList, loadSavedLists } from '$lib/stores/savedlists';
  import ResultItem from '$lib/components/ResultItem.svelte';
  import MemberResultItem from '$lib/components/MemberResultItem.svelte';
  import { refreshAccount } from '$lib/api/auth';
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import { page } from '$app/stores';

  import '../app.css';

  /**
   * Whether this is the members' deployment.
   *
   * A constant, not a store: it is what the bundle was built as, and reading
   * it as a constant is what stops a screen changing shape when a token
   * expires. See `AppMode` in `$lib/config/environment`.
   */
  const memberApp = env.mode === 'member';

  /**
   * The stored session, read before anything else in the app exists.
   *
   * Deliberately at the top of the script rather than in `onMount`, and the
   * ordering is the whole point: a child component's `onMount` runs *before*
   * its parent's, and at least one of them (`IndustryTree`) calls the API as
   * it mounts. Restoring in the layout's `onMount` would leave that call
   * unauthenticated — harmless in the public build, and in the members' build
   * refused by the guard in `api/client` as though nobody were signed in.
   *
   * This script runs during the layout's own initialisation, before any child
   * is created, so by the time anything can call the API the token is in the
   * store. `restoreSession` is a no-op without localStorage, so prerendering
   * is unaffected.
   */
  const restoredSession = restoreSession();

  /** Whether `goto` can be called yet — it only resolves once mounted. */
  let routerReady = false;

  /**
   * Send a signed-out member to the login screen, whenever they become one.
   *
   * The check in `onMount` covers arriving without a session. This covers
   * losing one with the app open, which is the case the two builds were meant
   * to make impossible to sit in: without it the guard above renders a spinner
   * and the app waits there forever.
   */
  $: if (routerReady && screen.kind === 'session') {
    void goto('/login', { replaceState: true });
  }

  // State for AI help dialog
  let showAIHelpDialog = false;

  // AI mode chat ref
  let aiModeChat: AIChatContent;

  // Handle AI search completed: just hide AI overlay, we're already in classic mode
  function handleAISearchExecuted() {
    aiModeActive.set(false);
  }

  // State for Apply button in data selection modal
  let isApplyingDataSelection = false;

  /**
   * Take or drop the category whose profile has the screen.
   *
   * The same write a row in IndustryTree does. No recount here: this picker
   * counts when it is closed, not on every tick.
   */
  function toggleProfiledCentroid() {
    const centroid = $industryProfile;
    if (!centroid) return;

    selectedIndustryCentroids.update((selected) => {
      const next = new Map(selected);
      const key = String(centroid.id);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, centroid.name);
      }
      return next;
    });
  }

  // Function to close modal and trigger search
  async function closeDataSelectionAndSearch() {
    if (isApplyingDataSelection) return; // Prevent double clicks

    isApplyingDataSelection = true;

    try {
      // Get Turnstile token first while dialog is still open
      await turnstile.getToken();

      // Only close dialog after token is obtained
      showDataSelectionModal.set(false);
      // Close mobile sidebar when running search
      sidebarOpen.set(false);
      // Small delay to ensure modal closes before search starts
      setTimeout(() => {
        submitFilters();
        isApplyingDataSelection = false;
      }, 100);
    } catch {
      notifications.error('Please complete the security check to continue');
      // Keep dialog open on error
      isApplyingDataSelection = false;
    }
  }

  // Wrapper function to close sidebar and run search
  logger.info(performance.now().toFixed(1), 'Loading', window.location.pathname);

  onMount(async () => {
    logger.info(performance.now().toFixed(1), 'Loaded', window.location.pathname);

    // What to do about the session, now that the router can be navigated.
    //
    // Reading it happens earlier, at the top of this script — see
    // `restoredSession`. Only the two things that need a mounted app are here:
    // the refresh, and the redirect.
    routerReady = true;

    if (restoredSession) {
      // What was stored is what the server said when they signed in, and some
      // of it goes stale under a session that is still perfectly valid: the
      // balance moves, and approval can be revoked.
      //
      // Deliberately not awaited — the page renders from what was stored and
      // corrects itself when the answer lands. A failure is left alone: the
      // token may be fine and the network briefly not, and signing somebody out
      // over one unanswered request is worse than a screen that is one refresh
      // behind.
      refreshAccount().catch(() => {});

      // The sidebar's lists, and the same lists the add-to-list menus offer.
      // After the session is restored, necessarily: the members' build refuses
      // an untokened request before it leaves, so this fetched any earlier
      // would be refused rather than merely anonymous.
      //
      // Not awaited — the sidebar shows its heading and fills in.
      void loadSavedLists();
    } else if (screen.kind === 'session') {
      // No session in the members' app, so there is nothing to show.
      //
      // This is what the two deployments buy. A single build had to answer
      // "signed in?" screen by screen, and answering "no" put a member on the
      // public app: masked rows, the visitor's endpoint, no sign of what had
      // happened. Here the shell is fixed and cannot answer "no", so an absent
      // or lapsed session has exactly one place to go.
      await goto('/login', { replaceState: true });
      return;
    }

    // A fresh page is a fresh selection.
    //
    // The stores are in memory and start empty anyway, so on a real reload this
    // changes nothing — but the app is also entered by navigating back to `/`
    // from checkout or the thank-you page, where they are not empty, and the
    // door would then be showing over somebody's previous search.
    //
    // Before the two lines below, both of which put something back: the shared
    // link restores a whole selection, and the customer's list restores whether
    // it is being subtracted.
    resetSelection();

    // The customer's own list survives a reload, because the code is the only
    // way back to it and nobody wants to upload the same file twice.
    restoreCustomerList();

    // Send page view for SPA navigation
    const currentPath = window.location.pathname + window.location.search;
    await telemetry.pageView(currentPath);

    // A shared link decides everything below it, so it is read first.
    //
    // It carries the mode as well as the selection, and it runs the search
    // itself: a recipient who has to press a button to see what they were sent
    // has been handed a form, not a result.
    const sharedCode = $page.url.searchParams.get(shareParam);
    if (sharedCode) {
      guidedActive.set(false);
      try {
        if (!(await openShare(sharedCode))) {
          notifications.error($_('share.notFound'));
          applicationMode.set('classic');
        }
      } finally {
        restoringShare = false;
      }
      return;
    }

    // Check for mode parameter in URL
    const urlMode = $page.url.searchParams.get('mode');
    const fromUrl = ['classic', 'neighbor', 'dataselection'] as const;
    if (urlMode && (fromUrl as readonly string[]).includes(urlMode)) {
      applicationMode.set(urlMode as (typeof fromUrl)[number]);
    } else if (!urlMode && $page.url.pathname === '/') {
      // The root path is classic mode.
      //
      // It also turned the assistant on, so the app opened onto the AI chat
      // and the guided screen was only reachable by dismissing it. The
      // assistant is an alternative way in, not the way in.
      applicationMode.set('classic');
    }

    // Check for autoSearch parameter
    const autoSearch = $page.url.searchParams.get('autoSearch');
    if (autoSearch === 'true' && urlMode === 'classic') {
      // Use a more reliable approach - wait for stores to be ready
      const attemptAutoSearch = () => {
        const centroids = $selectedIndustryCentroids;

        if ((centroids.size > 0 || $contactRoles.length > 0) && !$queryRunning) {
          submitFilters();
          // Clean up URL parameter to prevent repeated auto-searches
          const url = new URL(window.location.href);
          url.searchParams.delete('autoSearch');
          window.history.replaceState({}, '', url);
        }
      };

      // Try immediately, then with increasing delays if needed
      setTimeout(attemptAutoSearch, 100);
      setTimeout(() => {
        if (!$queryRunning && ($selectedIndustryCentroids.size > 0 || $contactRoles.length > 0)) {
          attemptAutoSearch();
        }
      }, 1000);
    }
  });

  /**
   * A shared link is being opened.
   *
   * Known before the first paint, because deciding what a link shows takes a
   * request and the screen must not show the wrong thing in the meantime. It
   * used to say the welcome page, which the mode defaulted to: a recipient met
   * a marketing panel for as long as the fetch and its search took, and then
   * the results replaced it. What they were sent is not a welcome. That mode
   * is gone now, but the wait it exposed is not, which is why this flag stays.
   *
   * That was fixed by rendering nothing, which traded a wrong screen for a
   * blank one — and this wait is not short: reading the share, restoring the
   * filters and running its search are three round trips, the last of them a
   * full search. A recipient watched an empty page for all of it with no sign
   * anything was happening. It now shows the same animation a search shows,
   * because that is what is happening.
   */
  let restoringShare = get(page).url.searchParams.has(shareParam);

  $: selectedMode = $applicationMode;

  /**
   * Where the customer is, as one value.
   *
   * This replaced eleven derived booleans — wizardHasTheScreen,
   * dataViewHasTheScreen, onTheDoor, selectionIsOnScreen, the three member
   * ones and four route tests — each computed from its own subset of nine
   * stores, and each having to stay consistent with the others by hand.
   *
   * The precedence now lives in `screenFor`, where it is asserted rather than
   * merely written down. See $lib/stores/screen and docs/refactor/screens.md.
   */
  $: screen = screenFor({
    path: $page.url.pathname,
    memberApp,
    signedIn: $signedIn,
    mode: $applicationMode,
    aiActive: $aiModeActive,
    picker: $showDataSelectionModal,
    industryProfile: $industryProfile !== null,
    restoringShare,
    listOpen: $openList !== null,
    historyOpen: $exportHistoryOpen,
    guideActive: $guidedActive,
    guideStep: $guidedStep,
    companyCount: $mixedResults.companies.length
  });

  /** What stands to the left of the content. */
  $: sidebar = sidebarFor(screen);

  /**
   * Whether the mobile drawer would have anything in it.
   *
   * Drives both the hamburger and the drawer's own contents, so the two cannot
   * disagree — which they did: the button was gated on `isFrontScreen` while
   * the drawer rendered filters regardless, so one screen had a button onto an
   * empty drawer and another had a drawer nobody could open.
   *
   * The assistant is the exception to `sidebar` alone. It is a `none` screen,
   * but in AI mode the drawer carries that screen's tips panel, which is the
   * only place they appear on a narrow window.
   */
  $: drawerHasContent = sidebar !== 'none' || $aiModeActive;

  /**
   * Offer the industry picker to somebody arriving with nothing chosen.
   *
   * # Why the shell asks and not the filter bar
   *
   * This used to run from FilterBar's onMount, which is a body component
   * deciding what should be on screen. The bar is built more than once per
   * visit — opening a company profile hides the sidebar and destroys it, coming
   * back builds it again — so the question was asked a second time over a search
   * the customer had already run, and the picker opened on top of their results.
   * It read as being dropped back at the front door with the search gone.
   *
   * Up here it is asked once per visit, because arriving is something that
   * happens to the shell rather than to a control inside it.
   *
   * The predicate itself is unchanged and still lives in $lib/stores/modal,
   * where it can be tested — a lifecycle hook cannot be.
   */
  let offeredDataSelection = false;

  $: if (
    !offeredDataSelection &&
    screen.kind === 'results' &&
    shouldOpenDataSelection({
      aiChatActive: $aiChatActive,
      hasSearched: $hasSearched,
      companyCount: $mixedResults.companies.length,
      industryCount: $selectedIndustryCentroids.size,
      contactRoleCount: $contactRoles.length,
      contactTitleCount: $contactTitles.length
    })
  ) {
    offeredDataSelection = true;
    showDataSelectionModal.set(true);
  }

  // Close data selection modal when entering checkout mode
  $: if (selectedMode === 'checkout') {
    showDataSelectionModal.set(false);
    isApplyingDataSelection = false;
  }

  // Reset applying state when modal is closed
  $: if (!$showDataSelectionModal) {
    isApplyingDataSelection = false;
  }

  // A "your filters changed — reload the results" affordance stood here: it
  // compared the live filters against the ones the last search ran with, set
  // `showReloadOverlay`, and offered `reloadResults` to re-run. Nothing ever
  // read the flag and nothing ever called the function, so the prompt has
  // never appeared. Removed rather than left looking live; re-adding it means
  // rendering the overlay, which is a UI decision.
  /**
   * What starting again will cost, this time.
   *
   * Built from what is actually held rather than listed in the locale file: a
   * customer with no uploaded list must not be warned that theirs will be
   * deleted, and one who has spent ten minutes matching a file has to be told
   * that it will.
   *
   * The list is a parameter rather than read from the store inside, so that the
   * markup calling it names `$customerList` and the dependency is tracked.
   */
  function resetPoints(list: CompanyList | null): string[] {
    const points = [$_('dialogs.startOver.clearFilters'), $_('dialogs.startOver.clearResults')];
    if (list) points.push($_('dialogs.startOver.deleteList'));
    return points;
  }

  function confirmStartOver() {
    resetDialog.set({ show: false });
    resetSelection();

    // Back to the door: the assistant closed, the picker closed, the guide
    // showing its first question again. This lives here rather than in the
    // reset store because `goto` only resolves inside a SvelteKit component.
    aiModeActive.set(false);
    showDataSelectionModal.set(false);
    guidedActive.set(true);
    applicationMode.set('classic');

    void goto('/');
  }
</script>

<svelte:head>
  <title>ainova</title>
  <meta name="description" content="Find and purchase contact information for Swiss companies" />
</svelte:head>

<!-- Global Notifications -->
<NotificationContainer />

<!--
  The members' app draws nothing at all without a session.

  Not only tidier than showing a shell that cannot be used — necessary. The app
  calls the API as it mounts (`IndustryTree` fetches the industry list), and a
  child mounts before its parent, so rendering this while the redirect to
  /login is still in flight fires requests the client then refuses and the
  screen reports as errors. Nothing renders, so nothing asks.

  Reactive on the session rather than read once: a token can lapse with the app
  open, and this is what stands in for the public app it can no longer become.
-->
{#if screen.kind === 'session'}
  <div class="flex min-h-screen items-center justify-center bg-white">
    <Loading />
  </div>
{:else if screen.kind === 'bare'}
  <slot />
{:else}
  <!-- Normal layout for other pages -->

  <!-- Mobile sidebar overlay -->
  {#if $sidebarOpen && !$aiModeActive && screen.kind !== 'profile'}
    <div class="fixed inset-0 z-[70] lg:hidden" role="dialog" aria-modal="true">
      <!-- Background overlay -->
      <div
        class="fixed inset-0 bg-gray-900/80"
        aria-hidden="true"
        on:click={() => sidebarOpen.set(false)}
      ></div>

      <!-- Sidebar panel -->
      <div
        class="fixed inset-y-0 left-0 z-[70] w-80 overflow-x-hidden overflow-y-auto bg-surface-chrome px-2 pb-2"
      >
        <div class="flex h-16 shrink-0 items-center justify-between px-2">
          <div class="flex items-center">
            <img class="ml-2 mt-1 h-7 w-auto" src="/logo.png" alt="ainova" />
          </div>
          <button
            type="button"
            class="-m-2.5 p-2.5 text-gray-400 hover:text-gray-600"
            on:click={() => sidebarOpen.set(false)}
          >
            <span class="sr-only">{$_('buttons.close')}</span>
            <svg
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Language Switcher for mobile -->
        <div class="mb-4 flex justify-center">
          <LanguageSwitcher />
        </div>

        <!--
          The drawer holds whatever the desktop sidebar would hold.

          It held the filters unconditionally, which made it wrong on every
          screen that is not the data view: a saved list or the export history
          opened the hamburger onto a filter panel for a search that is not on
          screen. And on the dashboard the hamburger was disabled outright,
          which is why the members' navigation — the lists, the exports, the
          doors — had no mobile route at all. `MemberNav` is `lg:flex`, so
          below that width it simply did not exist.

          Mirroring `sidebar` fixes both at once: the drawer is the same
          decision the desktop layout already makes, taken once.
        -->
        {#if $aiModeActive}
          <div class="mx-2 rounded-surface border border-hairline bg-surface-canvas p-4">
            <div
              class="mb-3 flex h-12 w-12 items-center justify-center rounded-surface bg-accent-50"
            >
              <svg
                class="h-6 w-6 text-accent-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h3 class="mb-1.5 text-sm font-semibold text-gray-900">{$_('ai.mode.sidebarTitle')}</h3>
            <p class="mb-4 text-xs leading-relaxed text-gray-600">
              {$_('ai.mode.sidebarDescription')}
            </p>
            <div class="space-y-2">
              {#each [$_('ai.mode.tip1'), $_('ai.mode.tip2'), $_('ai.mode.tip3')] as tip, i}
                <div class="flex items-start gap-2">
                  <span
                    class="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-accent-50 text-2xs font-semibold text-accent-600"
                    >{i + 1}</span
                  >
                  <span class="text-xs leading-relaxed text-gray-600">{tip}</span>
                </div>
              {/each}
            </div>
          </div>
        {:else if sidebar === 'memberNav'}
          <!--
            Closing on any click inside is blunt, and deliberately so. Every
            control in here either navigates or opens a dialog, and both want
            the drawer gone — a nav entry that leaves the drawer standing over
            the screen it just opened is the bug this is avoiding, not a
            refinement.
          -->
          <div
            role="presentation"
            on:click={() => sidebarOpen.set(false)}
            on:keydown={() => sidebarOpen.set(false)}
          >
            <MemberNav />
          </div>
        {:else if sidebar === 'filters'}
          <!-- Above the filters, the same order the desktop sidebar uses: the
               control sits directly over the thing it changes. -->
          <div class="mb-3 px-2">
            <Switcher />
          </div>

          <!--
            `fill={false}` here and nowhere else.

            The drawer is `inset-y-0` and scrolls itself, so a panel that fills
            the height it is given resolves to the whole viewport however
            little is in it — and its `flex-1` scroll area then pushes the
            search button to the bottom of that. A short set of filters left a
            screen's worth of empty rail above the button. The desktop asides
            keep `fill`, where the same arrangement is what pins the button to
            the panel's foot.
          -->
          {#if selectedMode === 'classic'}
            <Filters fill={false} on:showAIHelp={() => (showAIHelpDialog = true)} />
          {:else if screen.kind === 'neighbours'}
            <Filters neighbours={true} fill={false} />
          {:else if screen.kind === 'datapools'}
            <DataSelectionFilters />
          {/if}
        {/if}

        <!-- Mobile Summary - Hidden for testing -->
        <!-- {#if selectedMode === 'classic' || selectedMode === 'neighbor'}
        <div class="mt-4">
          <Summary />
        </div>
      {/if} -->
      </div>
    </div>
  {/if}

  <!--
    The shell is three rows: the top bar, the working area, and the summary bar.

    It used to be two columns — a full-height fixed sidebar, and everything
    else offset by its width. That put the top bar and the summary bar inside
    the right-hand column, so both stopped at the sidebar's edge, and the
    sidebar ran the full height of the window behind them. Nothing lined up
    across the seam, and the sidebar's own footer had to be aligned against a
    bar it was not a sibling of.

    As rows, the bars span the window and the sidebar sits between them, so the
    alignment is structural rather than arithmetic.
  -->
  <!-- White, not the grey canvas. See the note on the top bar: the frame is
       the dark thing now, so the data sits on white rather than in a well. -->
  <div class="flex h-[100dvh] flex-col bg-surface-panel">
    <!--
      Top bar for both mobile and desktop.

      `relative` is load-bearing, not decoration. The bar carried `z-40` while
      statically positioned, where z-index does nothing, and `<main>` below it
      is `relative` — so the later, positioned sibling painted over the earlier,
      static one. Anything hanging out of the bar landed behind the content: the
      search suggestions were invisible on the company profile, whose cards are
      opaque white, and unclickable everywhere else.
    -->
    <!--
      Navy, and no hairline under it.

      The shell had it backwards: the chrome was the lightest thing on screen
      and the content well behind the cards was the darkest, with hairlines
      doing all the separating. A frame is supposed to recede so the data can
      sit forward, and white chrome around a grey well does the opposite.

      So the bar is `--brand-navy` and the well below is white. The border goes
      with it — a hairline exists to separate two surfaces that are nearly the
      same colour, and these two are not.
    -->
    <div
      class="relative z-40 flex h-16 shrink-0 items-center gap-x-4 bg-navy px-4 sm:gap-x-6 sm:px-6 lg:px-8"
    >
      <!--
        Left group.

        Wrapped so the bar is three parts — left, centre, right — with the two
        outer ones growing equally. That is what centres the middle one: before
        this the search and the controls were two `flex-1` siblings, so they
        split the free space in half and the search centred inside its half,
        which reads as slightly left of centre.
      -->
      <div class="flex flex-1 items-center gap-x-4">
        <!--
        The logo belongs to the application, not to a filter panel.

        It lived in the sidebar and was duplicated here for the one mode that
        hides the sidebar, so the product's own mark came and went with the
        filters. Now that the top bar spans the window it is simply always
        here — and it has to be, because the sidebar no longer has a header
        to put it in.

        Still `lg:block`: below that width the bar already carries the
        hamburger, the mode switcher and the selected-lists control, and the
        drawer has its own logo.
      -->
        <!--
          The mark with its lightness inverted, for a dark bar.

          `logo.png` is six flat colours forming one ramp: lightness 22% to
          55%, hue 217 to 268 — dark blue-navy on the left, bright purple on
          the right. The left half of that ramp *is* this bar, which is why the
          glyph and the "AI" disappeared on it.

          `logo-light.png` reflects that lightness: what was darkest becomes
          lightest and what was lightest becomes darkest, so the ramp keeps its
          shape and runs the other way up. Hue and saturation are untouched, so
          each element keeps its own character; alpha is untouched, so shapes
          and antialiasing are exactly as drawn.

          The band stops at 84% rather than 100%. White would be brighter than
          anything else on the bar including the controls, and a mark that
          outshines the interface it labels is the same mistake as one that
          vanishes into it, only louder.

          Generated by scratchpad/invert_logo.py, which takes the band as two
          arguments and measures the source range rather than assuming it — so
          it still holds if the asset is redrawn.

          The drawer below keeps `logo.png`, because the drawer is light.
        -->
        <img class="hidden h-7 w-auto lg:block" src="/logo-light.png" alt="ainova" />

        <!--
          Shown when the drawer has something to open onto, which is the same
          test the drawer itself uses rather than a second opinion about it.

          It was `isFrontScreen`, so the dashboard hid it — correctly, at the
          time, because the drawer only ever held filters and the dashboard has
          no search to narrow. Now the drawer holds the members' navigation
          there, so the button is exactly what reaches it. `none` screens keep
          it hidden: the profile, the checkout, the picker and the guide have
          no sidebar on any width, so there is nothing behind it.
        -->
        <button
          type="button"
          class="chrome-ghost -m-2.5 rounded-control p-2.5 lg:hidden {drawerHasContent
            ? ''
            : 'invisible'}"
          on:click={() => sidebarOpen.set(true)}
          disabled={!drawerHasContent}
        >
          <span class="sr-only">{$_('nav.openMainMenu')}</span>
          <svg
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
            />
          </svg>
        </button>
      </div>

      <!--
        "Take me to X", from wherever the customer is.

        A company they can already name, or an industry — navigation rather
        than a mode, which is why it lives in the bar and not on a screen you
        would have to travel to in order to travel somewhere.

        Members only: identity search is refused without an account, and the
        public app's funnel is the search screen itself. No screen test beyond
        that — this branch is the full shell, so the two screens that draw
        their own frame are already excluded.
      -->
      {#if memberApp}
        <!--
          Centred in the bar rather than sitting after the logo.

          The middle of three parts. It takes its own width rather than a
          share of the leftover, and the groups either side grow equally around
          it — so it sits in the centre of the bar and stays there as the
          controls on the right change width, which they do: start-over, the
          customer list and the selected-lists button all come and go with the
          screen.
        -->
        <div class="flex w-full min-w-0 max-w-xl justify-center">
          <TopbarSearch />
        </div>
      {/if}

      <!--
        The controls, at the end of the bar.

        The mode switcher used to sit here below `lg`, on the reasoning that
        the sidebar is a drawer at that width and a switcher inside the drawer
        would be reachable only by opening the thing it changes. True, but it
        paid for that with the widest element in the bar at exactly the sizes
        with the least room — at medium widths it took a share of the row back
        and pushed everything else into the space left over.

        It lives in the drawer now, above the filters, which is where the
        desktop sidebar already puts it. The drawer is not only the filters any
        more — see the note on its contents — so opening it to change the mode
        is opening the panel the mode belongs to.
      -->
      <!--
        No `min-w-0` here, and that is the whole of it.

        `min-w-0` lets a flex item shrink past its own content, and the
        controls inside are `shrink-0` — so the group shrank while its children
        would not, and the account button was pushed out of the bar rather than
        the row giving way somewhere it could afford to. Without it the group
        floors at its content width, and the only item in the row that still
        carries `min-w-0` is the lookup, which is the one thing that should
        narrow as the window does.
      -->
      <div class="flex flex-1 items-center gap-2">
        <div class="ml-auto flex shrink-0 items-center gap-2">
          <!--
            The way back to the beginning.

            Shown wherever a selection has been started — including inside the
            assistant, which is where it was most missed: a conversation that
            has gone somewhere the customer did not want had no exit but a
            page reload. Not shown on the door itself, which is already the
            beginning.
          -->
          {#if showsSelection(screen) || isFrontScreen(screen) || screen.kind === 'assistant'}
            {#if !isFrontDoor(screen)}
              <StartOver />
            {/if}
          {/if}

          <!--
            The customer's own companies, and the switch that leaves them out
            of the selection.

            Only where the results are. The guide asks the same question as
            its last step, and a control in the bar above it would be the same
            thing offered twice on one screen.
          -->
          {#if showsSelection(screen)}
            <CustomerList />
          {/if}

          <!--
            Keeping this selection, which is a different act in each build.

            Both belong to the results and appear only with them: on the guide,
            the picker, the order form or the assistant there is no selection to
            keep yet.

            A visitor gets `ShareSelection` — a link to copy or mail, which is
            the only kind of keeping a public deployment has. It was labelled
            "Auswahl speichern" and shown to members too, where it promised the
            wrong thing: it mints a public share code rather than saving
            anything, and members have lists that are owned by the account and
            outlive the session. Bookmarking here means that.
          -->
          {#if showsSelection(screen)}
            {#if memberApp}
              <SaveSelectionToList />
            {:else}
              <ShareSelection />
            {/if}
          {/if}

          <!--
            Whose session this is, and the settings that belong to the person
            rather than to what is on screen: the language, and the way out.

            Last in the row, and only one of these is ever drawn. A visitor has
            no account to open, so the language keeps a control of its own for
            them — it is the whole of what the menu would have held.
          -->
          {#if $signedIn}
            <AccountMenu />
          {:else}
            <div class="hidden md:block">
              <LanguageSwitcher onNavy />
            </div>
          {/if}
        </div>
      </div>
    </div>

    <!--
      The working area: filters beside content.

      # The sidebar floats here rather than reaching the bars

      Both panels below carry a margin and a full border instead of running
      edge to edge with a single rule down their right side. Filling the rail
      the same tint as the summary bar and letting it meet that bar and the top
      bar made the three read as one drawn frame around the window — a box with
      a picture in it — which is a heavier thing than any of the three parts
      was meant to be. It is the reason the shell looked strange the moment the
      rail stopped being white.

      Inset, the count goes back to what it should be: two bars that span the
      window, and a panel sitting in the space between them. The gap is what
      says the panel is a thing on the page rather than part of its edge.
    -->
    <div class="flex min-h-0 flex-1">
      <!-- Desktop sidebar -->
      {#if sidebar === 'memberNav'}
        <!--
          The members' screens keep a sidebar where the guide has none, and it
          holds navigation rather than filters: there is no selection on these
          screens to narrow, and the four entries under "Listen erstellen" are
          the same doors as the tiles at the foot of the home screen.

          Desktop only, as the guide is. The mobile sidebar button is disabled
          while this screen is up, and the doors are reachable there as tiles.
        -->
        <!--
          Sized to its contents, and starting level with the title beside it.

          It stretched the full height of the working area, and the members'
          navigation is short — three entries, four doors, and however many
          saved lists there happen to be. On a tall window most of the rail was
          empty tint, which reads as a panel that failed to load rather than
          one that is simply short.

          `self-start` with the height following the content. The top margin is
          `mt-10` rather than `mt-3` so the panel's top edge lands on the same
          line as the heading in the content area: every screen this rail
          appears beside opens at `py-10`, so 2.5rem is where their first line
          begins.

          The cap is what makes an auto height safe. With no ceiling a customer
          with forty saved lists would grow the panel past the window and take
          the page's scroll with it. `100% - 3.25rem` is the row less this
          panel's own top and bottom margins, and `MemberNav` scrolls its own
          middle, so reaching the cap scrolls the lists rather than the screen.

          The filter panel keeps its full height deliberately — it pins the
          search button to its own foot, which needs a foot to pin to.
        -->
        <aside
          class="mx-3 mb-3 mt-10 hidden max-h-[calc(100%-3.25rem)] w-80 shrink-0 flex-col self-start rounded-surface border border-hairline bg-surface-chrome lg:flex"
        >
          <MemberNav />
        </aside>
      {:else if sidebar === 'none'}
        <!--
          No sidebar. The assistant, the guide, the order form, the industry
          picker and the company profile each take the full width: the first
          four ask one thing at a time, and a filter panel beside them is a
          second way to answer the question being asked. The profile is not a
          question at all — it is one company, and the panel narrows a search
          that is not on screen.
        -->
      {:else}
        <aside
          class="m-3 hidden w-80 shrink-0 flex-col rounded-surface border border-hairline bg-surface-chrome lg:flex"
        >
          <!--
            The logo has moved to the top bar, and with it the 64px spacer
            that held it. The hairline is a border now rather than an
            absolutely positioned div offset to clear a top bar that is no
            longer overhead.

            No scrolling here either. Filters scrolls its own middle section and
            pins the search button beneath it, so a scroll container wrapped
            around the whole panel would carry that button off the bottom of
            the window — which is the arrangement the button was being
            aligned against in the first place.
          -->
          <div class="border-b border-hairline px-2 py-3">
            <Switcher />
          </div>

          <div class="flex min-h-0 flex-1 flex-col">
            {#if selectedMode === 'classic'}
              <Filters on:showAIHelp={() => (showAIHelpDialog = true)} />
            {:else if screen.kind === 'neighbours'}
              <Filters neighbours={true} />
            {:else if screen.kind === 'datapools'}
              <DataSelectionFilters />
            {/if}
          </div>
        </aside>
      {/if}

      <!-- Main content -->
      <div class="flex min-h-0 min-w-0 flex-1 flex-col">
        <!-- Main content area -->
        <main class="relative min-h-0 flex-1 overflow-hidden">
          {#if screen.kind === 'profile'}
            <!--
              The profile, in the content area.

              First in the chain because it is a route and the rest are modes:
              the customer's search is still set up underneath, and it is
              waiting for them when they come back rather than being replaced.
            -->
            <slot />
          {:else if screen.kind === 'checkout'}
            <!--
              Ordering is a screen, not a dialog.

              It was a modal over a blurred copy of the results, which is the
              shape of an interruption — and this is the opposite: the last
              step of the thing the customer came to do. As a screen it gets
              the whole window, scrolls properly on a phone, and its close
              button returns to the mode they were in rather than dismissing
              an overlay.

              A column, so the header and footer stay put and the body scrolls
              between them: `.modal-body` already carries `flex-1
              overflow-y-auto` from when this was a dialog, which is the
              behaviour wanted here.
            -->
            <OrderForm />
          {:else if screen.kind === 'picker' || screen.kind === 'industryProfile'}
            <!--
              A category's profile takes this screen too, rather than floating
              over it — see IndustryInfo. Back returns to the picker with the
              selection and the open groups as they were.
            -->
            {#if $industryProfile}
              <IndustryInfo
                centroidId={$industryProfile.id}
                name={$industryProfile.name}
                selected={$selectedIndustryCentroids.has(String($industryProfile.id))}
                onToggle={toggleProfiledCentroid}
                onClose={() => industryProfile.set(null)}
              />
            {:else}
              <!--
                Choosing industries is a screen, on the shape every non-data
                screen has: a title, content at most 5xl and centred, a footer.
                It was the last dialog — a backdrop, a flown-in container and a
                body scrolling inside it — holding the same tree the guided
                search screen shows as a step.
              -->
              <ScreenView icon={Layers} title={$_('buttons.selectIndustries')}>
                <button
                  slot="actions"
                  type="button"
                  on:click={() => showDataSelectionModal.set(false)}
                  class="rounded-control px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                >
                  {$_('buttons.close')}
                </button>

                <!-- Search Bar -->
                <div class="mb-4">
                  <DataSelectionFilters />
                </div>

                <!--
                  What has been picked so far.

                  It was a tinted accent panel with a tick icon, a red "clear
                  all", white pills on the tint, and a list truncated to ten with
                  a "+N more" — inside a scroll area, so it both scrolled and
                  hid things. Four ideas about how to show a list of names.

                  Now: a quiet strip on the canvas, the accent only on the chips
                  because the accent means "selected" here, and no truncation.
                  The area scrolls if the selection is long, which is the one
                  mechanism the old one already had.
                -->
                {#if $selectedIndustryCentroids.size > 0}
                  <div class="mb-4 rounded-control border border-hairline bg-surface-canvas p-3">
                    <div class="mb-2 flex items-baseline justify-between gap-4">
                      <!--
                        Count first: the string is written to follow one —
                        "7 Branche(n) ausgewählt" — so putting the label ahead of
                        it reads as a heading with a stray number after it.
                      -->
                      <span class="text-2xs font-medium uppercase tracking-wide text-gray-500">
                        <span class="tabular-nums text-gray-900">
                          {$selectedIndustryCentroids.size}
                        </span>
                        {$_('buttons.industriesSelected')}
                      </span>

                      <button
                        type="button"
                        on:click={() => selectedIndustryCentroids.set(new Map())}
                        class="text-xs font-medium text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline"
                      >
                        {$_('buttons.clearAll')}
                      </button>
                    </div>

                    <div class="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">
                      {#each Array.from($selectedIndustryCentroids.entries()) as [id, name] (id)}
                        <span
                          class="inline-flex items-center gap-1.5 rounded-full border border-accent-200 bg-accent-50 py-0.5 pl-2.5 pr-1 text-xs text-accent-800"
                        >
                          {name}
                          <button
                            type="button"
                            aria-label={$_('buttons.close')}
                            on:click={() => {
                              selectedIndustryCentroids.update((map) => {
                                const updated = new Map(map);
                                updated.delete(id);
                                return updated;
                              });
                            }}
                            class="flex h-4 w-4 items-center justify-center rounded-full text-accent-500 transition-colors hover:bg-accent-200 hover:text-accent-900"
                          >
                            ×
                          </button>
                        </span>
                      {/each}
                    </div>
                  </div>
                {/if}

                <!-- Content -->
                <DataSelection />

                <svelte:fragment slot="footer">
                  <span class="mr-auto text-sm text-gray-600">
                    {$selectedIndustryCentroids.size}
                    {$_('stats.industrySegments')}
                  </span>
                  <button
                    type="button"
                    on:click={() => showDataSelectionModal.set(false)}
                    class="modal-btn-secondary rounded-control px-5 py-2.5 text-sm font-medium transition-colors duration-200"
                  >
                    {$_('buttons.cancel')}
                  </button>
                  <button
                    type="button"
                    on:click={closeDataSelectionAndSearch}
                    class="modal-btn-primary rounded-control px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:shadow-overlay disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
                    disabled={($selectedIndustryCentroids.size === 0 &&
                      $contactRoles.length === 0 &&
                      $contactTitles.length === 0) ||
                      isApplyingDataSelection}
                  >
                    {$_('buttons.applyFilters')}
                  </button>
                </svelte:fragment>
              </ScreenView>
            {/if}
          {:else if screen.kind === 'restoringShare'}
            <!-- See restoringShare: the recipient's search is already running. -->
            <div class="flex h-full items-center justify-center px-4 sm:px-6 lg:px-8">
              <Loading label={$_('loading.sharedSelection')} />
            </div>
          {:else if screen.kind === 'exportHistory'}
            <ExportHistory />
          {:else if screen.kind === 'list'}
            <!--
              A saved list, above the mode branch rather than inside classic
              mode's.

              A list is somewhere the customer navigated to, not a view of what
              they were looking at, so it takes the screen whichever mode they
              were in. Inside the classic branch it would show the sidebar
              entry as selected while the neighbour screen stayed up — the
              sidebar and the body disagreeing about what is open, which is
              exactly the fault one screen value exists to prevent.
            -->
            <SavedListView />
          {:else if selectedMode === 'classic'}
            {#if $aiModeActive}
              <!--
                The assistant is a screen like the others.

                It had a header of its own: a purple band with a white glyph, a
                white title and two white-on-purple buttons — the last coloured
                chrome in the app, and the one screen a customer could not place
                because nothing else looked like it. It is a `ScreenView` now,
                so it wears the same title, the same column and the same card as
                the guide, the order form and the industry picker.
              -->
              <ScreenView
                icon={Sparkles}
                title={$_('ai.chat.title')}
                subtitle={$_('ai.chat.subtitle')}
              >
                <svelte:fragment slot="actions">
                  {#if $aiChatActive}
                    <button
                      type="button"
                      on:click={() => aiModeChat?.resetChat()}
                      class="rounded-control p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900"
                      title={$_('ai.chat.reset')}
                    >
                      <RotateCcw class="h-4 w-4" />
                    </button>
                  {/if}
                  <!--
                    A close, not a mode switch.

                    The band carried "skip to manual filters →", and the top bar
                    forced the mode switcher into view whenever the assistant was
                    up, so this one screen offered two ways to change what the
                    app was doing while asking a question. Both are gone. What
                    remains is the same close every full-width screen has, and
                    the ordinary way out is not a button at all: the assistant
                    runs the search itself and the results take the window.
                  -->
                  <button
                    type="button"
                    on:click={() => aiModeActive.set(false)}
                    class="rounded-control p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900"
                    aria-label={$_('buttons.close')}
                  >
                    <X class="h-5 w-5" />
                  </button>
                </svelte:fragment>

                <!--
                  `-m-6` so the conversation reaches the card's edges: the input
                  row's rule and the scrolled messages both want the full width,
                  and the chat carries its own padding inside.

                  A bounded height rather than a growing card, because the
                  messages area scrolls itself and follows the newest line — it
                  can only do that inside a box with a height to overflow.
                -->
                <div class="-m-6 flex h-[32rem] max-h-[70vh] flex-col">
                  <AIChatContent
                    bind:this={aiModeChat}
                    fullWidth={true}
                    onSearchExecuted={handleAISearchExecuted}
                  />
                </div>
              </ScreenView>
            {:else}
              <!--
                Which front screen classic mode shows, decided here rather than
                inside Results.

                It is a shell question — what occupies the content area — and it
                was being answered by a body component, which is how choosing a
                door came to do nothing: Results drew the member home over the
                top of the step the door had just set. Up here the two are
                alternatives rather than one hiding the other.

                The guide takes the screen whenever it is active with nothing
                found. A signed-in customer gets the box instead of its *door
                step* only; from the first question onwards the guide runs for
                everybody.
              -->
              {#if isFrontScreen(screen)}
                {#if screen.kind === 'home'}
                  <MemberHome />
                {:else}
                  <GuidedSearch />
                {/if}
              {:else}
                <!--
                  The filter bar is hidden while the guided screen is up. It
                  summarises and edits the same filters the guide is collecting
                  step by step, so showing both offers two ways to change one
                  thing and undoes the point of a sequence.

                  That used to be a condition on the bar; it is now the shape of
                  the branch — the guide and the data view are the two things
                  this area can be.
                -->
                <FilterBar />
                <!-- <Summary /> Hidden for testing -->
                <!--
                  Which row the list draws is the build's to decide. The member
                  app draws counts and indicators; the public app draws the
                  masked preview row. Two components, one per deployment,
                  rather than one component asking a store what it is today —
                  and rather than a shell that swaps rows out from under a
                  customer whose token has just expired.
                -->
                <Results rowComponent={memberApp ? MemberResultItem : ResultItem} />
              {/if}
            {/if}
          {:else if screen.kind === 'neighbours'}
            <FilterBar />
            <!-- <Summary /> Hidden for testing -->
            <!--
              The same row as the classic list. This branch drew the public row
              for months because it simply did not say: `rowComponent` had a
              default, and a screen that forgot to choose got the masked
              preview. Similar-companies is a member search like any other.
            -->
            <Results rowComponent={memberApp ? MemberResultItem : ResultItem} />
          {:else if screen.kind === 'datapools'}
            <DataSelection />
          {:else}
            <Switcher />
          {/if}

          <!--
            The confirmations, both inside main so they are positioned against
            the working area rather than the whole window.
          -->
          {#if $modeSwitchDialog.show && $modeSwitchDialog.onCancel && $modeSwitchDialog.onConfirm}
            <ConfirmDialog
              title={$_('dialogs.switchMode.title')}
              message={$_('dialogs.switchMode.message', {
                values: {
                  from: $_(`modes.${$modeSwitchDialog.fromMode}`),
                  to: $_(`modes.${$modeSwitchDialog.toMode}`)
                }
              })}
              points={[
                $_('dialogs.switchMode.clearFilters'),
                $_('dialogs.switchMode.clearResults'),
                $_('dialogs.switchMode.clearUnsaved')
              ]}
              note={$_('dialogs.switchMode.cannotUndo')}
              confirmLabel={$_('dialogs.switchMode.confirm')}
              onConfirm={$modeSwitchDialog.onConfirm}
              onCancel={$modeSwitchDialog.onCancel}
            />
          {/if}

          {#if $resetDialog.show}
            <ConfirmDialog
              title={$_('dialogs.startOver.title')}
              message={$_('dialogs.startOver.message')}
              points={resetPoints($customerList)}
              note={$_('dialogs.startOver.cannotUndo')}
              confirmLabel={$_('dialogs.startOver.confirm')}
              onConfirm={confirmStartOver}
              onCancel={() => resetDialog.set({ show: false })}
            />
          {/if}
        </main>
      </div>
    </div>

    <!--
      The summary bar spans the window, beneath both the sidebar and the
      content. The sidebar's search button therefore sits above it rather than
      beside it, and the two no longer have to be aligned at all — which was
      being done by arithmetic, matching this bar's height against the
      sidebar's bottom padding, back when the bar stopped at the sidebar's edge.
    -->
    {#if screen.kind !== 'profile' && screen.kind !== 'checkout' && screen.kind !== 'assistant'}
      <PricingBar {selectedMode} canOrder={canOrder(screen)} />
    {/if}

    <!--
      A second bottom bar stood here. It rendered a bordered white band around
      `<slot name="content" />`, and nothing has ever filled that slot — so it
      drew a stray hairline across the foot of every screen, below the summary
      bar, containing nothing. Removed rather than left looking deliberate.
    -->
  </div>

  <!--
    The assistant's floating button belongs to the data view.

    It was gated on classic mode alone, so it hovered over the guide, over the
    industry picker and over anything else classic mode happened to be showing
    — offering a second way to build a search on top of a screen already
    asking for one, and covering the corner of a card while doing it.
  -->
  {#if isDataView(screen)}
    <AIChatOverlay />
  {/if}

  <!-- AI Help Dialog -->
  {#if showAIHelpDialog}
    <div
      class="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-sm"
      on:click|self={() => (showAIHelpDialog = false)}
    >
      <div class="dialog w-full max-w-md md:max-w-lg lg:max-w-xl">
        <div class="flex items-center justify-between">
          <h3 class="dialog-title mb-0 flex items-center">
            <svg
              class="mr-2 h-5 w-5 text-accent-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            {$_('filters.ai.helpDialog.title')}
          </h3>
          <button
            type="button"
            on:click={() => (showAIHelpDialog = false)}
            class="close-button"
            title={$_('buttons.close')}
          >
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div class="dialog-content mt-4">
          <p class="mb-4 text-gray-700">
            {$_('filters.ai.helpDialog.intro')}
          </p>

          <div class="space-y-4">
            <div>
              <h4 class="mb-2 font-semibold text-gray-900">
                {$_('filters.ai.helpDialog.howItWorks')}
              </h4>
              <p class="text-sm text-gray-600">
                {$_('filters.ai.helpDialog.howItWorksText')}
              </p>
            </div>

            <div>
              <h4 class="mb-2 font-semibold text-gray-900">
                {$_('filters.ai.helpDialog.examples')}
              </h4>
              <ul class="space-y-1 text-sm text-gray-600">
                <li>• {$_('filters.ai.helpDialog.example1')}</li>
                <li>• {$_('filters.ai.helpDialog.example2')}</li>
                <li>• {$_('filters.ai.helpDialog.example3')}</li>
              </ul>
            </div>

            <div>
              <h4 class="mb-2 font-semibold text-gray-900">{$_('filters.ai.helpDialog.tips')}</h4>
              <ul class="space-y-1 text-sm text-gray-600">
                <li>• {$_('filters.ai.helpDialog.tip1')}</li>
                <li>• {$_('filters.ai.helpDialog.tip2')}</li>
                <li>• {$_('filters.ai.helpDialog.tip3')}</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="dialog-actions justify-center">
          <button type="button" on:click={() => (showAIHelpDialog = false)} class="btn btn-primary">
            {$_('buttons.close')}
          </button>
        </div>
      </div>
    </div>
  {/if}
{/if}

<!-- End of normal layout -->

<style>
  :global(body) {
    margin: 0;
    padding: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  /* Mobile modal animations */
  :global(.animate-slide-up) {
    animation: slideUp 0.3s ease-out forwards;
  }

  @keyframes slideUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  /* Ensure proper safe area handling on mobile */
  @supports (padding: max(0px)) {
    :global(.safe-area-bottom) {
      padding-bottom: max(1rem, env(safe-area-inset-bottom));
    }
  }
</style>
